import React, { useEffect, useState } from 'react';
import AddGroupForm from './AddGroupForm';
import EditGroupForm from './EditGroupForm';
import '../css/ParticipantList.css';
import '../css/GroupList.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrashAlt, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import DeleteConfirmModal, { useDeleteConfirm } from './DeleteConfirmModal';
import { GoldLoader } from '../shared/Loaders';

const GroupList = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const deleteConfirm = useDeleteConfirm();
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/groups`);
      if (Array.isArray(res.data)) setGroups(res.data);
    } catch (err) {
      console.error('Failed to fetch groups', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleEdit = (group) => {
    setGroupToEdit(group);
  };

  const handleCloseEditModal = () => {
    setGroupToEdit(null);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleDelete = (group) => {
    deleteConfirm.ask({ id: group._id, name: group.group_name || 'this group' });
  };

  const confirmDelete = async () => {
    const id = deleteConfirm.pending?.id;
    if (!id) return;
    deleteConfirm.setBusy(true);
    try {
      await axios.delete(`${API_URL}/ems/groups/${id}`);
      deleteConfirm.clear();
      fetchGroups();
    } catch {
      deleteConfirm.setError('Delete failed');
      deleteConfirm.setBusy(false);
    }
  };

  // Filter groups by search term
  const filteredGroups = groups.filter((group) =>
    (group.group_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const sorted = sortConfig.key
    ? [...filteredGroups].sort((a, b) => {
        const aVal = (a.group_name || '').toLowerCase();
        const bVal = (b.group_name || '').toLowerCase();
        const result = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? result : -result;
      })
    : filteredGroups;

  // Pagination calculations
  const effectivePerPage = itemsPerPage >= sorted.length ? Math.max(sorted.length, 1) : itemsPerPage;
  const totalPages = Math.max(Math.ceil(sorted.length / effectivePerPage), 1);
  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentItems = sorted.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;
    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  if (loading) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="ems-groups-page p-4">
      {/* Header with Search on left and Add Button on right */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 ems-list-toolbar">
        <div className="d-flex align-items-center w-25">
          <div className="me-2" style={{ flex: "1" }}>
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search groups"
              style={{ border: "1px solid #d1cfcf" }}
            />
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            background: '#000',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '5px',
            border: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          + Add Group
        </button>
      </div>

      {/* Table */}
        <div className="group-header">
          <div
            className={`sort-header ${sortConfig.key === 'group_name' ? 'sorted' : ''}`}
            onClick={() => toggleSort('group_name')}
          >
            Group Name <FontAwesomeIcon icon={sortIcon('group_name')} className="sort-icon" />
          </div>
          <div>Actions</div>
        </div>

        {loading ? (
          <GoldLoader label="Loading..." variant="section" />
        ) : filteredGroups.length === 0 ? (
          <div className="p-4 text-center text-muted">
            No groups available
          </div>
        ) : (
          currentItems.map((group) => (
            <div className="group-row" key={group._id}>
              <div>{group.group_name || '—'}</div>
              <div className="group-actions">
                <button
                  className="btn btn-sm action-btn"
                  onClick={() => handleEdit(group)}
                  title="Edit group"
                >
                  <FontAwesomeIcon icon={faPen} />
                </button>
                <button
                  className="btn btn-sm action-btn"
                  onClick={() => handleDelete(group)}
                  title="Delete group"
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </div>
            </div>
          ))
        )}

        {/* Pagination inside table */}
        {sorted.length > 0 && (
          <div className="participant-pagination">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '13px', color: '#666' }}>Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', fontSize: '13px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="pagination-box">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {getVisiblePages().map((num) => (
                <button
                  key={num}
                  className={`pagination-btn ${num === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

      {/* Add Group Modal */}
      {showAddModal && (
        <AddGroupForm
          onClose={handleCloseAddModal}
          onGroupAdded={fetchGroups}
        />
      )}

      {/* Edit Group Modal */}
      {groupToEdit && (
        <EditGroupForm
          onClose={handleCloseEditModal}
          onGroupUpdated={fetchGroups}
          groupToEdit={groupToEdit}
        />
      )}
      {deleteConfirm.pending && (
        <DeleteConfirmModal
          name={deleteConfirm.pending.name}
          error={deleteConfirm.error}
          busy={deleteConfirm.busy}
          onConfirm={confirmDelete}
          onCancel={deleteConfirm.clear}
        />
      )}
    </div>
  );
};

export default GroupList;
